import { makeVar } from "@apollo/client";
import type { ReactNode } from "react";
import { useState, useEffect, useContext } from "react";
import { AuthContext } from "./context";
import { NOT_REMOVE_COOKIE } from "~/const/cookie";
import { profileIdVar } from "~/lib/apollo/link";
import { G_SubscriptionStatus, useFetchUserLazyQuery } from "~/lib/graphql";
import { autoLoginScript } from "~/util/auto-login-script";
import { getCookie } from "~/util/get-cookie";
import { isBot } from "~/util/is-bot";
import { uriConverter } from "~/util/uri-converter";

/**
 * @return isLoggedIn ログイン済みだと`true` 未ログインだと`false`
 * @return execAuth リダイレクト処理関数(未ログイン時にPF側の認証画面にリダイレクトさせる)
 */
export const useAuth = () => {
  return useContext(AuthContext);
};

type AuthProviderProps = {
  children: ReactNode;
};

export const isLoadingAuthVar = makeVar(!isBot());

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isFetchedCookie, setIsFetchedCookie] = useState(false);
  const [exec, { data, loading: isLoading }] = useFetchUserLazyQuery();

  // ログイン処理を実行
  // 現在のページURLをpath=移行に付与することでlogin後戻ってこれる
  const execAuth = () => {
    const backUrl = uriConverter.encode(location.href);
    const redirectUrl = `${process.env.NEXT_PUBLIC_LOGIN_ENDPOINT}${backUrl}`;
    window.location.href = redirectUrl;
  };

  const fanzaPlanStatus = data?.user.fanzaPlanStatus.status ?? G_SubscriptionStatus.Unsubscribe;
  const isAvailableTvPlus =
    fanzaPlanStatus === G_SubscriptionStatus.Subscribe || fanzaPlanStatus === G_SubscriptionStatus.WaitingUnsubscribe;

  const value = {
    isLoggedIn,
    execAuth,
    userId: data?.user.id,
    servicePlan: data?.user.planStatus.servicePlan,
    email: data?.user.mail,
    wallet: data?.user.wallet,
    isAvailableTvPlus,
    viewableRating: data?.user.profiles.find(({ isParent }) => isParent)?.viewableRating,
    profileId: data?.user.profiles.find(({ isParent }) => isParent)?.id,
    planStatus: data?.user.planStatus,
    isAffiliateOwner: data?.user.isAffiliateOwner,
    isFirstSubscribe: data?.user.isFirstSubscribe,
  };

  useEffect(() => {
    if (isBot()) return;
    const isLoggedIn = !!getCookie(NOT_REMOVE_COOKIE.LOGIN_SESSION_ID.KEY);
    const isAutoLogin = !!getCookie(NOT_REMOVE_COOKIE.HAS_ALT_HASH.KEY);

    // `login_session_id`が存在しない、かつ、自動ログインが有効（`has_althash`が存在する）場合は`自動ログインの処理`を scriptタグ に埋め込む
    if (!isLoggedIn && isAutoLogin) {
      autoLoginScript();
    }

    setIsLoggedIn(isLoggedIn);
    setIsFetchedCookie(true);
  }, []);

  useEffect(() => {
    if (isBot()) return;
    if (!isFetchedCookie) return;
    if (isLoggedIn) {
      exec();
      return;
    }
    isLoadingAuthVar(false);
  }, [isFetchedCookie]);

  useEffect(() => {
    if (isBot()) return;
    if (isLoading || !data) return;
    const profileId = data.user.profiles.find((profile) => profile.isParent)?.id ?? "";
    profileIdVar(profileId);
    isLoadingAuthVar(false);
  }, [data, isLoading]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
